import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/authService';
import { logger } from '@/utils/logger';
import { getRememberMe, getRememberedEmail } from '@/utils/authStorage';

interface LoginFormProps {
  onSubmit: (data: LoginData) => void;
  isLoading?: boolean;
  error?: string;
  successMessage?: string;
}

interface LoginData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading = false, error, successMessage }) => {
  const navigate = useNavigate();
  const { theme, systemTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: '',
    rememberMe: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginData>>({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    try {
      const rememberedEmail = getRememberedEmail();
      const remember = getRememberMe();
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail || '',
        rememberMe: remember,
      }));
    } catch (error) {
      logger.debug('[LoginForm] Erro ao carregar dados de lembrar de mim:', error);
    }
  }, []);

  const currentTheme = theme === 'system' ? systemTheme : theme;
  const isDarkMode = currentTheme === 'dark';

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginData> = {};

    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const next = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: next }));
    if (errors[name as keyof LoginData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      setForgotPasswordMessage('Por favor, digite seu email');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      logger.log('[LoginForm] Solicitando recuperação de senha para:', forgotPasswordEmail);
      await authService.requestPasswordReset(forgotPasswordEmail);
      logger.log('[LoginForm] Email de recuperação solicitado com sucesso');
      setForgotPasswordMessage('Email de recuperação enviado! Verifique sua caixa de entrada.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
        setForgotPasswordMessage('');
      }, 3000);
    } catch (error: any) {
      logger.error('[LoginForm] Erro ao solicitar recuperação de senha:', error);
      const errorMessage = error?.message || 'Erro ao enviar email. Tente novamente.';
      setForgotPasswordMessage(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-xl p-8 border border-border">
        {/* Header */}
        <div className="text-center mb-8">
          {mounted && (
            <img
              src={isDarkMode 
                ? "/imgs/logos/EscriturIA-logo-horizontal.png"
                : "/imgs/logos/EscriturIA-logo-horizontal-sem-slogan.png"
              }
              alt="EscriturIA Logo"
              className="h-16 w-auto object-contain mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold text-secondary font-serif">
            Bem-vindo de volta
          </h1>
          <p className="text-brand-text-muted mt-2">
            Entre na sua conta para continuar
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-brand-error-bg border border-brand-error-border rounded-lg">
            <p className="text-brand-error-text text-sm">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand-text-primary mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                errors.email ? 'border-destructive' : ''
              }`}
              placeholder="seu@email.com"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-brand-error-text">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-brand-text-primary mb-2">
              Senha
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                  errors.password ? 'border-destructive' : ''
                }`}
                placeholder="Sua senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary transition-colors duration-200"
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-brand-error-text">{errors.password}</p>
            )}
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="rememberMe"
                checked={!!formData.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 text-secondary bg-background border-border rounded focus:ring-secondary focus:ring-2"
                disabled={isLoading}
              />
              <span className="ml-2 text-sm text-brand-text-secondary">Lembrar de mim</span>
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-brand-accent hover:text-brand-accent-hover hover:underline transition-colors duration-200"
            >
              Esqueceu a senha?
            </button>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="hero"
            size="lg"
            disabled={isLoading}
            className="w-full text-lg px-8 py-6"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-brand-text-muted text-sm">
            Não tem uma conta?{' '}
            <button 
              type="button"
              onClick={() => navigate('/register')}
              className="text-brand-accent hover:text-brand-accent-hover font-semibold hover:underline transition-colors duration-200"
            >
              Criar conta
            </button>
          </p>
        </div>
      </div>

      {/* Modal de Recuperação de Senha */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-md border border-border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-brand-text-primary">
                Recuperar Senha
              </h3>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setForgotPasswordMessage('');
                }}
                className="text-brand-text-muted hover:text-brand-text-primary transition-colors"
              >
                ✕
              </button>
            </div>

            <p className="text-brand-text-secondary text-sm mb-4">
              Digite seu email e enviaremos um link para redefinir sua senha.
            </p>

            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label htmlFor="forgotEmail" className="block text-sm font-medium text-brand-text-primary mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotEmail"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200"
                  placeholder="seu@email.com"
                  disabled={forgotPasswordLoading}
                />
              </div>

              {forgotPasswordMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  forgotPasswordMessage.includes('enviado') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-brand-error-bg text-brand-error-text border border-brand-error-border'
                }`}>
                  {forgotPasswordMessage}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  className="flex-1 py-2 px-4 bg-card text-foreground border border-border rounded-lg hover:bg-card/80 transition-colors duration-200"
                  disabled={forgotPasswordLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="hero"
                  size="default"
                  disabled={forgotPasswordLoading}
                  className="flex-1"
                >
                  {forgotPasswordLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    'Enviar'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
